import { Card } from "@/components/ui/card";
import { WeeklyPlanItem } from "@/types/calculator";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingDown, Flame } from "lucide-react";

interface ResultsChartsProps {
  weeklyPlan: WeeklyPlanItem[];
}

const ResultsCharts = ({ weeklyPlan }: ResultsChartsProps) => {
  return (
    <div className="space-y-4">
      {/* Weight Loss Progression Chart */}
      <Card className="p-4 border-card-border rounded-2xl shadow-soft">
        <div className="flex items-center gap-2 mb-4">
          <TrendingDown className="w-5 h-5 text-accent" />
          <h3 className="text-sm font-semibold text-foreground">Weight Loss Projection</h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={weeklyPlan.slice(0, 12)}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="week" 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              label={{ value: "Week", position: "insideBottom", offset: -5, fill: "hsl(var(--muted-foreground))" }}
            />
            <YAxis 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              label={{ value: "Weight Loss (kg)", angle: -90, position: "insideLeft", fill: "hsl(var(--muted-foreground))" }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: "hsl(var(--card))", 
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
                fontSize: "12px"
              }}
              formatter={(value: number) => [`${value.toFixed(2)} kg`, "Cumulative Loss"]}
            />
            <Line 
              type="monotone" 
              dataKey="cumulativeWeightChange" 
              stroke="hsl(var(--accent))" 
              strokeWidth={2}
              dot={{ fill: "hsl(var(--accent))", r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Weekly Calorie Burn Chart */}
      <Card className="p-4 border-card-border rounded-2xl shadow-soft">
        <div className="flex items-center gap-2 mb-4">
          <Flame className="w-5 h-5 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Weekly Calorie Burn</h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={weeklyPlan.slice(0, 12)}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="week" 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              label={{ value: "Week", position: "insideBottom", offset: -5, fill: "hsl(var(--muted-foreground))" }}
            />
            <YAxis 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              label={{ value: "Calories", angle: -90, position: "insideLeft", fill: "hsl(var(--muted-foreground))" }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: "hsl(var(--card))", 
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
                fontSize: "12px"
              }}
              formatter={(value: number) => [`${Math.round(value)} kcal`, "Weekly Burn"]}
            />
            <Bar 
              dataKey="estimatedCalories" 
              fill="hsl(var(--primary))"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};

export default ResultsCharts;
